import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

/** Image types the folder logo endpoint accepts. */
export const FOLDER_LOGO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

/** Allowed video extensions for vault media uploads. */
export const VIDEO_EXTENSIONS: string[] = ['.mp4', '.mkv', '.3gp', '.avi', '.webm', '.mov', '.flv'];

/** Allowed image extensions for vault media uploads. */
export const IMAGE_EXTENSIONS: string[] = ['.jpeg', '.jpg', '.png', '.webp', '.gif', '.svg'];

/** Combined list of allowed media extensions for vault uploads. */
export const ALLOWED_MEDIA_EXTENSIONS: string[] = [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS];

/**
 * Checks if a file or file name has an allowed media extension.
 */
export function isAllowedMediaExtension(fileOrName: string | File): boolean {
  if (!fileOrName) return false;
  const fileName = typeof fileOrName === 'string' ? fileOrName : fileOrName.name || '';
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) return false;
  const ext = fileName.substring(lastDotIndex).toLowerCase();
  return ALLOWED_MEDIA_EXTENSIONS.includes(ext);
}

/** Server-side cap for a folder logo, in bytes. */
export const FOLDER_LOGO_MAX_SIZE = 5242880;

/** Server-side cap on ids per bulk-delete request. */
export const BULK_DELETE_CHUNK_SIZE = 100;

/** Server-side cap on ids per bulk feature/unfeature request. */
export const BULK_FEATURE_CHUNK_SIZE = 100;

@Injectable({
  providedIn: 'root'
})
export class VaultService {

  constructor(private http: HttpClient) { }

   create_album_url = environment.host + "vault-folder"
   get_all_media_item = environment.host + "vault-media/folder/"
   get_album_media = environment.host + "vault-media/"


/**
 * Creates a folder, optionally as a subfolder and/or with a logo.
 *
 * With a logo the body must be multipart. The Content-Type header is
 * deliberately not set: the browser has to write the multipart boundary
 * itself, and Angular already leaves it alone for a FormData body.
 */
createVaultAlbum(albumData: any, logoFile?: File | null): Observable<any> {
  if (!logoFile) {
    return this.http.post(this.create_album_url, albumData);
  }

  const form = new FormData();
  Object.keys(albumData || {}).forEach((key) => {
    const value = albumData[key];
    if (value !== undefined && value !== null && value !== '') {
      form.append(key, value);
    }
  });
  form.append('file', logoFile);

  return this.http.post(this.create_album_url, form);
}

/**
 * Renames a folder and/or replaces its logo. At least one of the two must be
 * supplied; the server rejects an empty patch with "Nothing to update!".
 */
updateVaultAlbum(
  folderId: string,
  folderName?: string | null,
  logoFile?: File | null
): Observable<any> {
  const url = `${this.create_album_url}/${folderId}`;

  if (!logoFile) {
    return this.http.patch(url, { folderName });
  }

  const form = new FormData();
  if (folderName) {
    form.append('folderName', folderName);
  }
  form.append('file', logoFile);

  return this.http.patch(url, form);
}

/**
 * Lists folders at one level. Without parentFolderId the server returns root
 * folders only; with it, that folder's direct children only — never the whole
 * subtree, so browsing three levels means three calls.
 *
 * startDate/endDate filter on folder creation date. They are plain YYYY-MM-DD
 * calendar dates, not timestamps — the backend applies IST day boundaries
 * itself. Send both or neither; one alone applies no filter.
 */
getAllCreatedAlbums(
  page: number,
  limit: number,
  searchQuery?: string,
  parentFolderId?: string | null,
  isAll: boolean = true,
  startDate?: string,
  endDate?: string
) {
  let params = new HttpParams()
    .set('isAll', String(isAll))
    .set('page', page.toString())
    .set('limit', limit.toString());

  if (searchQuery) {
    params = params.set('searchQuery', searchQuery);
  }

  if (parentFolderId) {
    params = params.set('parentFolderId', parentFolderId);
  }

  if (startDate && endDate) {
    params = params.set('startDate', startDate).set('endDate', endDate);
  }

  return this.http.get(`${this.create_album_url}`, { params });
}

/** Single folder record, for a detail view or a deep link. */
getFolderById(folderId: string): Observable<any> {
  return this.http.get(`${this.create_album_url}/${folderId}`);
}

/**
 * Clears a folder's logo. A logo cannot be removed through the PATCH by
 * sending an empty file — this is the only way.
 */
removeFolderLogo(folderId: string): Observable<any> {
  return this.http.delete(`${this.create_album_url}/${folderId}/logo`);
}


vaultPreSignedUrls(files: any[], folderId: string): Observable<any> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${localStorage.getItem('guestToken')}`,
  });

  const body = {
    folderId,
    files: files.map(file => ({
      fileName: file.fileName,
      totalChunks: file.totalChunks,
      fileSize: file.fileSize
    }))
  };

  return this.http.post(`${environment.host}vault-media/presigned-url`, body, { headers });
}

completeUpload(files: any[], folderId: string): Observable<any> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${localStorage.getItem("guestToken")}`,
  });

  const body = {
    folderId,
    files: files.map((f) => ({
      uniqueFileName: f.uniqueFileName,
      uploadId: f.uploadId,
      mediaId: f.mediaId,
      parts: f.parts.map((p: any) => ({
        PartNumber: p.PartNumber,
        ETag: p.ETag,
      })),
    }))
  };

  return this.http.post(`${environment.host}vault-media/complete-upload`, body, { headers });
}



/**
 * Lists one folder's media. `filter` must be sent to the server rather than
 * applied to the response: the listing is paginated and mediaCount is derived
 * from the same query, so filtering the current page client-side would report
 * the wrong totals and break paging.
 *
 * startDate/endDate are plain YYYY-MM-DD calendar dates, not timestamps — the
 * backend expands them to IST day boundaries itself. Send both or neither;
 * one alone makes it skip date filtering.
 */
getVaultMedia(
  folderId: string,
  page: number,
  limit: number,
  searchQuery?: string,
  startDate?: string,
  endDate?: string,
  filter?: "all" | "featured"
) {
  const params: any = {
    isAll: true,
    page,
    limit,
  };

  if (searchQuery) params.searchQuery = searchQuery;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (filter) params.filter = filter;

  return this.http.get(`${this.get_all_media_item}${folderId}`, { params });
}

/**
 * Marks a file as featured. This is one shared, admin-curated flag — not a
 * per-user favourite — so every viewer sees the same value. Idempotent.
 */
featureMedia(mediaId: string): Observable<any> {
  return this.http.post(
    `${this.get_album_media}${mediaId}/feature`,
    {}
  );
}

/** Clears the featured flag along with featuredById/featuredAt. Idempotent. */
unfeatureMedia(mediaId: string): Observable<any> {
  return this.http.delete(`${this.get_album_media}${mediaId}/feature`);
}

/**
 * Features or unfeatures up to BULK_FEATURE_CHUNK_SIZE files at once.
 *
 * Both directions are POST — there is no DELETE variant, because a request
 * body on DELETE is unreliable through proxies.
 *
 * Partial success is normal: ids that no longer resolve come back in
 * skipped[] while the rest still apply, so callers must read updatedIds
 * rather than assuming the whole batch changed. Permissions, unlike bulk
 * delete, are checked for the request as a whole.
 */
bulkSetFeatured(mediaIds: string[], isFeatured: boolean): Observable<any> {
  const path = isFeatured ? "bulk-feature" : "bulk-unfeature";
  return this.http.post(`${environment.host}vault-media/${path}`, {
    mediaIds,
  });
}


/** Cascades server-side: the folder, its subfolders, and all their files. */
deleteVaultAlbum(id: string) {
  return this.http.delete(`${this.create_album_url}/${id}`);
}

deleteSingleMedia(id:string){
  return this.http.delete(`${this.get_album_media}${id}`)
}

/**
 * Renames a file. Send the base name only — the server keeps the original
 * extension either way, and echoes back the stored name.
 */
renameMedia(mediaId: string, fileName: string): Observable<any> {
  return this.http.patch(`${this.get_album_media}${mediaId}`, { fileName });
}

/**
 * Deletes up to BULK_DELETE_CHUNK_SIZE files. Partial success is normal: the
 * response carries deletedIds plus a skipped[] for files the user does not own
 * or that no longer exist, so callers must not assume the whole batch went.
 */
bulkDeleteMedia(mediaIds: string[]): Observable<any> {
  return this.http.post(`${environment.host}vault-media/bulk-delete`, {
    mediaIds,
  });
}

getMediaById(mediaId: string): Observable<any> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${localStorage.getItem('guestToken')}`,
  });
  return this.http.get(`${this.get_album_media}${mediaId}`, { headers });
}

/**
 * Searches vault media by text (Text to Image multimodal search).
 */
searchVaultMediaByText(
  q: string,
  limit: number = 20,
  cursor?: string,
  startDate?: string,
  endDate?: string
): Observable<any> {
  const token = localStorage.getItem('guestToken');
  const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
  let params = new HttpParams().set('q', q).set('limit', limit.toString());
  if (cursor) params = params.set('cursor', cursor);
  if (startDate) params = params.set('startDate', startDate);
  if (endDate) params = params.set('endDate', endDate);

  return this.http.get(`${environment.host}vault-media/search/text`, { params, headers });
}

/**
 * Searches vault media by image (Image to Image multimodal search).
 */
searchVaultMediaByImage(
  file: File,
  limit: number = 20,
  cursor?: string,
  startDate?: string,
  endDate?: string
): Observable<any> {
  const token = localStorage.getItem('guestToken');
  const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('limit', limit.toString());
  if (cursor) formData.append('cursor', cursor);
  if (startDate) formData.append('startDate', startDate);
  if (endDate) formData.append('endDate', endDate);

  return this.http.post(`${environment.host}vault-media/search/image`, formData, { headers });
}

}

